// @ts-nocheck
import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetBody, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Search, MessageSquare, Lightbulb, Link2, Building, Calendar } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { formatDate } from '@/lib/formatting';
import { toast } from 'sonner';

interface LinkFeedbackIdeaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'feedback-to-idea' | 'idea-to-feedback';
  sourceId: string;
  onSuccess?: () => void;
}

export function LinkFeedbackIdeaDialog({ 
  open, 
  onOpenChange, 
  mode,
  sourceId,
  onSuccess 
}: LinkFeedbackIdeaDialogProps) {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [existingLinks, setExistingLinks] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const isFeedbackMode = mode === 'feedback-to-idea';
  const title = isFeedbackMode ? 'Link Feedback to Ideas' : 'Link Idea to Feedback';
  const description = isFeedbackMode 
    ? 'Select one or more ideas to link this feedback to'
    : 'Select one or more feedback items to link to this idea';

  useEffect(() => {
    if (open && workspace) {
      loadItems();
      loadExistingLinks();
    }
  }, [open, workspace, mode, sourceId]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(item => {
        if (isFeedbackMode) {
          // Searching ideas
          return item.title?.toLowerCase().includes(query) ||
                 item.description?.toLowerCase().includes(query) ||
                 item.status?.toLowerCase().includes(query);
        } else {
          // Searching feedback
          return item.content?.toLowerCase().includes(query) ||
                 item.author?.toLowerCase().includes(query) ||
                 item.source?.toLowerCase().includes(query);
        }
      });
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [searchQuery, items]);

  const loadItems = async () => {
    if (!workspace) return;

    try {
      setLoading(true);

      if (isFeedbackMode) {
        // Load ideas
        const { data, error } = await supabase
          .from('ideas')
          .select('id, title, description, status, priority, created_at')
          .eq('workspace_id', workspace.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setItems(data || []);
      } else {
        // Load feedback
        const { data, error } = await supabase
          .from('feedback')
          .select(`
            id, 
            content, 
            contributor, 
            source, 
            sentiment, 
            created_at,
            client:clients(id, name)
          `)
          .eq('workspace_id', workspace.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setItems(data || []);
      }
    } catch (error: any) {
      console.error('Error loading items:', error);
      toast.error('Failed to load items', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const loadExistingLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('feedback_ideas')
        .select('feedback_id, idea_id')
        .or(isFeedbackMode 
          ? `feedback_id.eq.${sourceId}` 
          : `idea_id.eq.${sourceId}`);

      if (error) throw error;

      const linkedIds = new Set<string>();
      data?.forEach(link => {
        linkedIds.add(isFeedbackMode ? link.idea_id : link.feedback_id);
      });
      
      setExistingLinks(linkedIds);
      setSelectedIds(new Set(linkedIds));
    } catch (error: any) {
      console.error('Error loading existing links:', error);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Calculate links to add and remove
      const toAdd = Array.from(selectedIds).filter(id => !existingLinks.has(id));
      const toRemove = Array.from(existingLinks).filter(id => !selectedIds.has(id));

      // Add new links
      if (toAdd.length > 0) {
        const linksToInsert = toAdd.map(id => ({
          feedback_id: isFeedbackMode ? sourceId : id,
          idea_id: isFeedbackMode ? id : sourceId,
          linked_at: new Date().toISOString(),
          linked_by: membership?.user_id
        }));

        const { error: insertError } = await supabase
          .from('feedback_ideas')
          .insert(linksToInsert);

        if (insertError) throw insertError;
      }

      // Remove unselected links
      if (toRemove.length > 0) {
        for (const id of toRemove) {
          const { error: deleteError } = await supabase
            .from('feedback_ideas')
            .delete()
            .match(isFeedbackMode 
              ? { feedback_id: sourceId, idea_id: id }
              : { feedback_id: id, idea_id: sourceId });

          if (deleteError) throw deleteError;
        }
      }

      toast.success('Links updated successfully', {
        description: `Added ${toAdd.length}, removed ${toRemove.length}`
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating links:', error);
      toast.error('Failed to update links', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getSentimentBadge = (sentiment: string) => {
    const styles = {
      'positive': 'bg-green-50 text-green-600 border-green-200',
      'negative': 'bg-red-50 text-red-600 border-red-200',
      'neutral': 'bg-blue-50 text-blue-600 border-blue-200'
    };
    return styles[sentiment as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
      case 'Backlog':
        return 'secondary';
      case 'Reviewed':
      case 'Planned':
      case 'In Prioritization':
      case 'Selected':
      case 'In Development':
      case 'In Progress':
      case 'Shipped':
      case 'Completed':
      case 'Merged':
        return 'default';
      case 'Archived':
      case 'Cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl">
        <SheetHeader>
          <SheetTitle className="flex items-center space-x-2">
            <Link2 className="h-5 w-5" />
            <span>{title}</span>
          </SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={isFeedbackMode ? 'Search ideas...' : 'Search feedback...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Selection Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {selectedIds.size} selected
            </span>
            {selectedIds.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear all
              </Button>
            )}
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto border rounded-lg min-h-[400px]">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                {isFeedbackMode ? (
                  <Lightbulb className="h-12 w-12 text-muted-foreground mb-4" />
                ) : (
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                )}
                <p className="text-muted-foreground">
                  {searchQuery ? 'No items match your search' : 'No items available'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredItems.map(item => {
                  const isSelected = selectedIds.has(item.id);
                  const isExisting = existingLinks.has(item.id);

                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleSelection(item.id)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                        isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      {isFeedbackMode ? (
                        // Render Idea
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <h4 className="font-medium">{item.title}</h4>
                                {item.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            {isExisting && (
                              <Badge variant="outline" className="ml-2">
                                Already linked
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground pl-8">
                            <Badge variant={getStatusColor(item.status) as any}>
                              {item.status}
                            </Badge>
                            {item.priority && (
                              <span>Priority: {item.priority}</span>
                            )}
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(item.created_at, regionalSettings)}</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Render Feedback
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <p className="text-sm line-clamp-2">{item.content}</p>
                              </div>
                            </div>
                            {isExisting && (
                              <Badge variant="outline" className="ml-2">
                                Already linked
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground pl-8">
                            {item.client?.name && (
                              <div className="flex items-center space-x-1">
                                <Building className="h-3 w-3" />
                                <span>{item.client.name}</span>
                              </div>
                            )}
                            {item.contributor && (
                              <span>{item.contributor}</span>
                            )}
                            {item.source && (
                              <Badge variant="outline">{item.source}</Badge>
                            )}
                            {item.sentiment && (
                              <Badge className={`${getSentimentBadge(item.sentiment)} text-xs`}>
                                {item.sentiment}
                              </Badge>
                            )}
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(item.created_at, regionalSettings)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetBody>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : `Link ${selectedIds.size} ${isFeedbackMode ? 'Ideas' : 'Feedback Items'}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}